import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { App as CapApp } from '@capacitor/app';
import { SplashScreen } from '@capacitor/splash-screen';
import { supabase } from '@/lib/supabaseClient';
import { ThemeProvider } from 'next-themes';
import { useTheme } from 'next-themes';
import { Toaster } from '@/components/ui/sonner';
import Login from '@/pages/Login';
import AuthCallback from '@/pages/AuthCallback';
import SmartActions from '@/pages/SmartActions';
import Stock from '@/pages/Stock';
import CustomerInsights from '@/pages/CustomerInsights';
import CreditCustomers from '@/pages/CreditCustomers';
import StockMaster from '@/pages/StockMaster';
import YourSavings from '@/pages/YourSavings';
import Appendix from '@/pages/Appendix';
import Landing from '@/pages/Landing';
import TermsOfService from '@/pages/TermsOfService';
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import RefundPolicy from '@/pages/RefundPolicy';
import ResetPassword from '@/pages/ResetPassword';
import Settings from '@/pages/Settings';
import BillingHub from '@/pages/BillingHub';
import PurchaseHub from '@/pages/PurchaseHub';
import DashboardInsights from '@/pages/DashboardInsights';
import AdvancedReports from '@/pages/AdvancedReports';
import AccountingGST from '@/pages/AccountingGST';
import MFAEnroll from '@/pages/MFAEnroll';
import MFAVerify from '@/pages/MFAVerify';
import Layout from '@/components/Layout';
import LockedPage from '@/components/LockedPage';
import ErrorBoundary from '@/components/ErrorBoundary';
import BiometricLock from '@/components/BiometricLock';
import { clearAuth } from '@/lib/api';
import { LanguageProvider } from '@/contexts/LanguageContext';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { OfflineProvider } from '@/contexts/OfflineContext';
import { RoleProvider } from '@/contexts/RoleContext';
import { TIER_ACCESS, normaliseTier } from '@/lib/tierConfig';
import { useBrowserShortcutGuard } from '@/hooks/useBrowserShortcutGuard';
import '@/App.css';

// Lazy-loaded: these three portals are role-gated (platform_admin / chain_owner /
// supplier) and never rendered for the store-owner/cashier/manager persona, so
// splitting them out of the main bundle has zero effect on the primary app's
// happy path — only these roles pay a one-time chunk-fetch on first navigation.
const AdminDashboard = lazy(() => import('@/pages/AdminDashboard'));
const ChainLayout = lazy(() => import('@/components/ChainLayout'));
const SupplierLayout = lazy(() => import('@/components/SupplierLayout'));

const PageLoadingFallback = () => (
  <div className="flex items-center justify-center h-screen">
    <div className="text-lg text-muted-foreground">Loading...</div>
  </div>
);

// Lightweight wrapper for the /mfa/* pages — user must have a token but NOT need aal2 yet
const MFARoute = ({ children }) => {
  const token = localStorage.getItem('kiranaflow_token') || sessionStorage.getItem('kiranaflow_token');
  if (!token) return <Navigate to="/login" replace />;
  return children;
};

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;

  // If user needs MFA but hasn't verified yet, send to verify page
  const needsMFA = !user.mfa_exempt && user.aal !== 'aal2';
  if (needsMFA) return <Navigate to="/mfa/verify" replace />;

  return children;
};

// Metadata for each locked page — the real page component is passed as children
const LOCKED_PAGE_META = {
  '/smart-actions':     { featureName: 'Smart Actions',     requiredTier: 'Growth',     description: 'AI-powered restocking alerts, expiry warnings, and savings recommendations' },
  '/customer-insights': { featureName: 'Customer Insights', requiredTier: 'Growth',     description: 'Customer analytics, loyalty tiers, spending patterns, and credit management' },
  '/dashboard':         { featureName: 'Dashboard',         requiredTier: 'Growth',     description: 'KPI overview, sales trends, and AI-powered insights' },
  '/savings':           { featureName: 'Your Savings',      requiredTier: 'Growth',     description: 'Track cost savings from smart restocking recommendations' },
  '/reports':           { featureName: 'Advanced Reports',  requiredTier: 'Enterprise', description: 'Sales analytics, category breakdowns, and custom date ranges' },
  '/accounting':        { featureName: 'Accounting & GST',  requiredTier: 'Enterprise', description: 'P&L statement, Day Book, GST Returns, and Balance Sheet' },
};

// Wraps the real page in a blur+lock overlay for lower tiers.
// children is the actual page component — it renders and makes real API calls,
// so the preview shows the exact same design. After upgrading the user sees the
// identical UI, just unblurred.
const TierProtectedRoute = ({ path, children }) => {
  const { user, storeProfile } = useAuth();
  const role = user?.role;
  const rawTier = normaliseTier(storeProfile?.membership_tier);
  // Mirror the JWT hook cap so cashiers/managers can't bypass via direct URL
  const tier = role === 'cashier' ? 'starter'
             : role === 'manager' ? (rawTier === 'enterprise' ? 'growth' : rawTier)
             : rawTier;
  if (!TIER_ACCESS[tier].pages.includes(path)) {
    // Cashiers get a hard redirect — no blur preview, no data leakage
    if (role === 'cashier') return <Navigate to="/pos" replace />;
    const meta = LOCKED_PAGE_META[path];
    if (meta) return <LockedPage {...meta}>{children}</LockedPage>;
    return <Navigate to="/pos" replace />;
  }
  return children;
};

function ImpersonateRedirect() {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token');
  if (token) {
    try {
      const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
      const userData = {
        id:              payload.sub,
        role:            payload.role,
        owner_id:        payload.owner_id,
        store_id:        payload.store_id,
        shop_name:       payload.shop_name  || '',
        owner_name:      payload.owner_name || '',
        mobile:          '',
        membership_tier: payload.membership_tier || 'starter',
      };
      sessionStorage.setItem('kiranaflow_impersonation', 'true');
      sessionStorage.setItem('kiranaflow_token', token);
      sessionStorage.setItem('kiranaflow_user', JSON.stringify(userData));
    } catch { /* malformed token — fall through to login */ }
  }
  return <Navigate to="/" replace />;
}

const _decodeJwt = (token) => {
  try { return JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))); } catch { return {}; }
};

function AppRoutes() {
  const { user, storeProfile } = useAuth();
  const navigate = useNavigate();
  useBrowserShortcutGuard();

  // Biometric gate: only fire when there's a real aal2 session (or mfa_exempt user)
  // in localStorage — not for impersonation tabs or unauthenticated states.
  const _token = localStorage.getItem('kiranaflow_token');
  const _payload = _token ? _decodeJwt(_token) : {};
  const hasBiometricSession = !!_token
    && sessionStorage.getItem('kiranaflow_impersonation') !== 'true'
    && (_payload.aal === 'aal2' || _payload.mfa_exempt === true);

  // Safety net: if Supabase fires PASSWORD_RECOVERY anywhere (e.g. when the
  // redirect_to landed at root instead of /auth/reset-password), send the user
  // to the correct page so the reset form can handle the session.
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        navigate('/auth/reset-password', { replace: true });
      }
    });
    return () => subscription.unsubscribe();
  }, [navigate]);

  // Hide native splash screen once the app has rendered
  useEffect(() => {
    SplashScreen.hide({ fadeOutDuration: 300 }).catch(() => {});
  }, []);

  // Android hardware back button: go back in history or exit app on root routes
  useEffect(() => {
    const ROOT_PATHS = new Set(['/pos', '/stock', '/login', '/home', '/']);
    const handler = CapApp.addListener('backButton', ({ canGoBack }) => {
      if (canGoBack && !ROOT_PATHS.has(window.location.pathname)) {
        navigate(-1);
      } else {
        CapApp.exitApp();
      }
    });
    return () => { handler.then(h => h.remove()); };
  }, [navigate]);

  const homeRedirect = () => {
    if (!user) return '/login';
    if (user.role === 'platform_admin') return '/admin';
    if (user.role === 'chain_owner') return '/chain/dashboard';
    if (user.role === 'supplier') return '/supplier/stores';
    return '/pos';
  };

  const PortalRoute = ({ requiredRole, children }) => {
    if (!user) return <Navigate to="/login" replace />;
    if (user.role !== requiredRole) return <Navigate to="/login" replace />;
    const needsMFA = !user.mfa_exempt && user.aal !== 'aal2';
    if (needsMFA) return <Navigate to="/mfa/verify" replace />;
    return children;
  };

  return (
    <BiometricLock
      hasSession={hasBiometricSession}
      onUsePassword={() => { clearAuth(); navigate('/login'); }}
    >
    <Suspense fallback={<PageLoadingFallback />}>
    <Routes>
      <Route path="/home"    element={<Landing />} />
      <Route path="/terms"   element={<TermsOfService />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/refund"  element={<RefundPolicy />} />
      <Route path="/login" element={user ? <Navigate to={homeRedirect()} replace /> : <Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/auth/reset-password" element={<ResetPassword />} />
      <Route path="/mfa/enroll" element={<MFARoute><MFAEnroll /></MFARoute>} />
      <Route path="/mfa/verify" element={<MFARoute><MFAVerify /></MFARoute>} />
      <Route path="/admin" element={
        user?.role === 'platform_admin'
          ? (user.aal === 'aal2' ? <AdminDashboard /> : <Navigate to="/mfa/verify" replace />)
          : <Navigate to="/login" replace />
      } />

      <Route
        path="/"
        element={
          user ? (
            <ProtectedRoute>
              <RoleProvider>
                <Layout />
              </RoleProvider>
            </ProtectedRoute>
          ) : (
            <Landing />
          )
        }
      >
        <Route index element={user ? <Navigate to={homeRedirect()} replace /> : null} />
        <Route path="dashboard"          element={<TierProtectedRoute path="/dashboard"><DashboardInsights /></TierProtectedRoute>} />
        <Route path="savings"            element={<TierProtectedRoute path="/savings"><YourSavings /></TierProtectedRoute>} />
        <Route path="smart-actions"      element={
          <TierProtectedRoute path="/smart-actions">
            <SmartActions />
          </TierProtectedRoute>
        } />
        <Route path="stock"              element={<Stock />} />
        <Route path="customer-insights"  element={
          <TierProtectedRoute path="/customer-insights">
            <CustomerInsights />
          </TierProtectedRoute>
        } />
        <Route path="credit-customers"   element={<CreditCustomers />} />
        <Route path="credit"             element={<Navigate to="/credit-customers" replace />} />
        <Route path="reports"            element={<TierProtectedRoute path="/reports"><AdvancedReports /></TierProtectedRoute>} />
        <Route path="accounting"         element={<TierProtectedRoute path="/accounting"><AccountingGST /></TierProtectedRoute>} />
        <Route path="appendix"           element={<Appendix />} />
        <Route path="pos"                element={<BillingHub />} />
        <Route path="sales-history"      element={<Navigate to="/pos?view=history" replace />} />
        <Route path="purchases"          element={<PurchaseHub />} />
        <Route path="purchases/history"  element={<Navigate to="/purchases?view=history" replace />} />
        <Route path="stock-master"       element={<StockMaster />} />
        <Route path="settings"           element={<Settings />} />
      </Route>
      <Route path="/chain/*" element={
        <PortalRoute requiredRole="chain_owner"><ChainLayout /></PortalRoute>
      } />
      <Route path="/supplier/*" element={
        <PortalRoute requiredRole="supplier"><SupplierLayout /></PortalRoute>
      } />
      <Route path="/impersonate" element={<ImpersonateRedirect />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
    </BiometricLock>
  );
}

function ThemedToaster() {
  const { resolvedTheme } = useTheme();
  return <Toaster position="top-right" richColors theme={resolvedTheme} />;
}

function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="light" storageKey="kiranaflow-theme" enableSystem>
      <BrowserRouter>
        <ErrorBoundary>
          <AuthProvider>
            <OfflineProvider>
              <LanguageProvider>
                <AppRoutes />
                <ThemedToaster />
              </LanguageProvider>
            </OfflineProvider>
          </AuthProvider>
        </ErrorBoundary>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
